import clsx from "clsx";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigationContext } from "web/react/components/navigation/navigation.context";
import { AutoComplete } from "web/react/components/navigation/search-bar/auto-complete";
import { useAutoCompleteContext } from "web/react/components/navigation/search-bar/auto-complete/auto-complete.context";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { HStack } from "web/react/emo/h-stack";
import { closeSearch, openSearch } from "web/redux/ducks/app-header-bar";
import { hideMainNavigation } from "web/redux/ducks/main-navigation";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import strings from "web/script/utils/strings";
import { AppHeaderBarSearchInputSerializer } from "web/types/serializers";
import * as styles from "./search-bar.css";

const EVENT_CATEGORY = "top_nav";

function extractSelectedGenderState(state: ReduxStoreState): string {
    return strings.capitalize(state.mainNavigationReducer.selectedGender);
}
interface SearchBarProps {
    searchInput: AppHeaderBarSearchInputSerializer;
    isMobile: boolean;
}
export function SearchBar({ searchInput, isMobile }: SearchBarProps): React.ReactElement {
    const {
        isSearchInputFocused,
        query,
        setSearchInputFocused,
        updateAutocomplete,
        searchInputRef,
        onKeyDown,
        onSubmit,
        onInput,
    } = useAutoCompleteContext();
    const stateGender = useSelector(extractSelectedGenderState);
    const selectedGender = stateGender || searchInput.gender;
    const [initialInteracted, setInitialInteracted] = useState<boolean>(false);
    const [loaded, setLoaded] = useState<boolean>(false);
    const { setIsSearchOpen } = useNavigationContext();
    const dispatch = useDispatch();
    const { setShowMenu } = useNavigationContext();

    /* initialize the search value on initial load; query doesn't change */
    useEffect(() => {
        if (searchInput.query) {
            updateAutocomplete({ query: searchInput.query });
        }
        setLoaded(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput.query]);

    useEffect(() => {
        updateAutocomplete({
            gender: selectedGender,
        });
    }, [updateAutocomplete, selectedGender]);

    function onBlurSearchInput(): void {
        setSearchInputFocused(false);
        setIsSearchOpen(false);
        dispatch(closeSearch());
    }

    function onFocusSearchInput(): void {
        setSearchInputFocused(true);
        setShowMenu(null);
        dispatch(openSearch());
        if (!initialInteracted) {
            setInitialInteracted(true);
        }

        analytics.event(EVENT_CATEGORY, "search_focused", "search_bar");
        dispatch(hideMainNavigation());
    }

    function onClickCancelButton(): void {
        dispatch(closeSearch());
        setIsSearchOpen(false);
        analytics.event("header", "clicked", "burger_menu_search_button_close");
    }

    return (
        <>
            <span className={styles.searchWrapper}>
                <form action={searchInput.action_url} onSubmit={onSubmit}>
                    <HStack>
                        <div className={styles.inputWrapper}>
                            {!isMobile && (
                                <button type="submit" className={styles.searchButton}>
                                    <SVGIcon className={styles.searchIcon} name="search" />
                                </button>
                            )}
                            <input
                                type="text"
                                className="hidden"
                                name="term"
                                defaultValue={loaded ? query : searchInput.query}
                            />
                            <input
                                type="text"
                                className="hidden"
                                name="gender"
                                defaultValue={selectedGender}
                            />
                            {/* set autoComplete="off" autoCorrect="off" to disable QuickType on iOS */}
                            <input
                                className={styles.input}
                                aria-label={gettext("header.product_search.label")}
                                type="search"
                                autoComplete="off"
                                autoCorrect="off"
                                name="q"
                                placeholder={
                                    selectedGender === "Men"
                                        ? gettext("header.search.placeholder_for_men")
                                        : gettext("header.search.placeholder_for_women")
                                }
                                onBlur={onBlurSearchInput}
                                onFocus={onFocusSearchInput}
                                onInput={onInput}
                                onChange={onInput}
                                onKeyDown={onKeyDown}
                                ref={searchInputRef}
                                value={loaded ? query : searchInput.query}
                                autoFocus={isMobile}
                            ></input>
                            {isMobile && (
                                <button type="submit" className={styles.searchButton}>
                                    <SVGIcon className={styles.searchIcon} name="search" />
                                </button>
                            )}
                        </div>
                        {isMobile && (
                            <button
                                type="button"
                                className={styles.closeButton}
                                onClick={onClickCancelButton}
                            >
                                <SVGIcon className={styles.closeIcon} name="cross-thin" />
                            </button>
                        )}

                        {/* Native keyboard `search` button wouldn't work w/o this w/o displaying a [type=submit] element (can't have display: none) */}
                        {/* <input type="submit" /> */}
                    </HStack>
                </form>
            </span>

            <div
                className={clsx(styles.autoCompleteWrapper, {
                    hidden: !isSearchInputFocused,
                })}
            >
                <AutoComplete />
            </div>
        </>
    );
}
